import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Badge } from "react-bootstrap";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import { motion } from "framer-motion";
import PropertyTypeDataList from "./property-data.json";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const scaleIn = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

const titleAnimation = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: "easeOut",
    },
  },
};

export default function PropertyTypes() {
  // State to manage the property type data
  const [PropertyTypeData] = useState(PropertyTypeDataList);
  const titleRef = useRef(null);

  useEffect(() => {
    const titleEl = titleRef.current; // Cache ref
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-lines");
          } else {
            entry.target.classList.remove("animate-lines");
          }
        });
      },
      { threshold: 0.3 }
    );

    if (titleEl) observer.observe(titleEl);

    return () => {
      if (titleEl) observer.unobserve(titleEl); // Use cached ref
    };
  }, []);

  return (
    <section className="section overflow-hidden">
      <Container>
        {/* Section Title */}
        <Row>
          <Col md={12}>
            <motion.div className="section-title-box"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={titleAnimation}
            >
              <h1 className="section-title" ref={titleRef}>
                <motion.span className="text-primary"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  What's
                </motion.span>{" "}
                <motion.span
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  Inside
                </motion.span>
                <div className="title-line before-line" />
                <div className="title-line after-line" />
              </h1>
            </motion.div>
          </Col>
        </Row>

        {/* Property Type Slider */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <Row>
            <Col md="12">
              <Swiper
                className="property-type-slider custom-slider pb-5"
                spaceBetween={50}
                pagination={{ dynamicBullets: true }}
                modules={[Autoplay, Pagination]}
                autoplay={{ delay: 25000 }}
                breakpoints={{
                  1: { slidesPerView: 1 },
                  480: { slidesPerView: 2 },
                  768: { slidesPerView: 3 },
                  992: { slidesPerView: 4 },
                  1200: { slidesPerView: 5 },
                }}
              >
                {PropertyTypeData.propertyTypeList.map((data, i) => (
                  <SwiperSlide key={i}>
                    <motion.div
                      variants={scaleIn}
                      whileHover={{
                        scale: 1.05,
                        boxShadow: "0 15px 40px rgba(0, 0, 0, 0.15)",
                      }}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 20,
                      }}
                      className="property-type-item text-center p-4 rounded-4"
                    >
                      <motion.div className="property-icon mx-auto mb-3" transition={{ duration: 0.8, ease: "easeInOut" }}>
                        <img src={require(`../../images/property-icon/${data.propertyIcon}`)} alt={data.categoryName} className="img-fluid" width={54}/>
                      </motion.div>
                      <motion.h5 className="mb-2" variants={fadeInUp}>
                        {data.categoryName}
                      </motion.h5>
                      <motion.div variants={fadeInUp} whileHover={{ scale: 1.1 }}>
                        <Badge pill bg="light text-dark badge-lg">{data.categoryListing}</Badge>
                      </motion.div>
                    </motion.div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </Col>
          </Row>
        </motion.div>
      </Container>
    </section>
  );
}
